(() => {
  if (customElements.get('oilogic-product-gallery')) return;

/*
 * <oilogic-product-gallery> — progressive enhancement for the PDP media gallery.
 *
 * The server renders every slide side by side in a flex track, so the first
 * slide is visible without JS (overflow: hidden clips the rest). This element
 * upgrades the track to a draggable, snapping Embla carousel — loaded on
 * demand via the shared singleton loader (embla-loader.js) — and syncs the
 * thumbnail rail + prev/next arrows with it. The rail is a native scroll
 * container (wheel and touch pan work for free); a light pointer handler adds
 * mouse drag-to-scroll, and the active thumb is nudged into view only when it
 * sits outside the rail, so user scrolling is never fought. Events are
 * namespaced oilogic:gallery:*.
 */
class OilogicProductGallery extends HTMLElement {
  #ctrl = new AbortController();
  #embla = null;
  #railDragged = false;

  connectedCallback() {
    this.viewport = this.querySelector('[data-carousel-viewport]');
    this.rail = this.querySelector('[data-thumbs-viewport]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.prevBtn = this.querySelector('[data-gallery-prev]');
    this.nextBtn = this.querySelector('[data-gallery-next]');
    if (!this.viewport || this.slides.length < 2) return;

    const { signal } = this.#ctrl;
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.select(i), { signal });
    });
    this.prevBtn?.addEventListener('click', () => this.select(this.index - 1), { signal });
    this.nextBtn?.addEventListener('click', () => this.select(this.index + 1), { signal });
    this.#enableRailDrag(signal);

    this.index = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains('product-gallery__slide--active'))
    );

    this.#updateUi();

    if (typeof window.EmblaCarousel === 'function') {
      this.#init();
    } else if (typeof window.oilogicEmbla === 'function') {
      window.oilogicEmbla(this.dataset.emblaUrl)
        .then(() => this.#init())
        .catch(() => {});
    }
  }

  disconnectedCallback() {
    this.#ctrl.abort();
    this.#embla?.destroy();
    this.#embla = null;
  }

  #init() {
    if (this.#embla || typeof window.EmblaCarousel !== 'function') return;

    this.#embla = window.EmblaCarousel(this.viewport, {
      align: 'start',
      loop: false,
      startIndex: this.index,
      duration: 22,
    });

    this.#embla.on('select', () => this.#syncFromEmbla());
    this.#embla.on('reInit', () => this.#syncFromEmbla());

    this.dataset.ready = '';
  }

  select(index) {
    const count = this.slides.length;
    this.index = ((index % count) + count) % count;

    if (this.#embla) {
      this.#embla.scrollTo(this.index);
    } else {
      this.#updateUi();
      this.#emitChange();
    }
  }

  #syncFromEmbla() {
    this.index = this.#embla.selectedScrollSnap();
    this.#updateUi();
    this.#emitChange();
  }

  /*
   * Mouse drag-to-scroll for the thumbnail rail. Touch pointers already pan
   * the native scroll container, so only mouse input is handled here. A small
   * movement threshold separates drags from clicks, and a genuine drag
   * swallows the click that follows so it never selects a thumb.
   */
  #enableRailDrag(signal) {
    if (!this.rail) return;

    // Native image drag-and-drop would cancel the pointer stream mid-drag.
    this.rail.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    let startY = 0;
    let startScroll = 0;
    let dragging = false;

    this.rail.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        startY = event.clientY;
        startScroll = this.rail.scrollTop;
        dragging = true;
        this.#railDragged = false;
      },
      { signal }
    );

    this.rail.addEventListener(
      'pointermove',
      (event) => {
        if (!dragging) return;
        const delta = event.clientY - startY;
        if (!this.#railDragged && Math.abs(delta) < 5) return;
        this.#railDragged = true;
        this.rail.setPointerCapture(event.pointerId);
        this.rail.scrollTop = startScroll - delta;
      },
      { signal }
    );

    const endDrag = () => {
      dragging = false;
    };
    this.rail.addEventListener('pointerup', endDrag, { signal });
    this.rail.addEventListener('pointercancel', endDrag, { signal });

    this.rail.addEventListener(
      'click',
      (event) => {
        if (!this.#railDragged) return;
        this.#railDragged = false;
        event.preventDefault();
        event.stopPropagation();
      },
      { capture: true, signal }
    );
  }

  /* Drive the rail like koakids' slick thumbnail slider (focusOnSelect +
     infinite): selecting a thumb aligns it to the rail's top edge so the thumbs
     that follow it slide into view; selecting the last thumb loops the rail
     back to the first. The browser clamps scrollTop at the rail's end, so this
     is a no-op when every thumb already fits with no overflow. */
  #syncRailScroll() {
    if (!this.rail || !this.thumbs.length) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduceMotion ? 'auto' : 'smooth';

    // Last thumb → loop back to the top (infinite-style wrap).
    if (this.index === this.slides.length - 1) {
      if (this.rail.scrollTop > 1) this.rail.scrollBy({ top: -this.rail.scrollTop, behavior });
      return;
    }

    // Otherwise align the active thumb to the rail's top, revealing the thumbs
    // after it. A negative delta pulls an earlier, scrolled-off thumb back down.
    const thumb = this.thumbs[this.index];
    if (!thumb) return;
    const delta = thumb.getBoundingClientRect().top - this.rail.getBoundingClientRect().top;
    if (Math.abs(delta) <= 1) return;

    this.rail.scrollBy({ top: delta, behavior });
  }

  #updateUi() {
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('product-gallery__slide--active', i === this.index);
    });

    this.thumbs.forEach((thumb, i) => {
      const active = i === this.index;
      thumb.classList.toggle('product-gallery__thumb--active', active);
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    this.#syncRailScroll();

    if (this.prevBtn) this.prevBtn.disabled = this.#embla ? !this.#embla.canScrollPrev() : this.index === 0;
    if (this.nextBtn) {
      this.nextBtn.disabled = this.#embla
        ? !this.#embla.canScrollNext()
        : this.index === this.slides.length - 1;
    }
  }

  #emitChange() {
    this.dispatchEvent(
      new CustomEvent('oilogic:gallery:change', { bubbles: true, detail: { index: this.index } })
    );
  }
}

customElements.define('oilogic-product-gallery', OilogicProductGallery);
})();
